/**
 * Utility functions for handling transfer proof data
 */

/**
 * Check if a URL is an image file
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is an image file
 */
export const isImageFile = (url) => {
    if (!url) return false;
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const lowerUrl = url.toLowerCase();
    return imageExtensions.some(ext => lowerUrl.includes(ext));
};

/**
 * Check if a URL is a PDF file
 * @param {string} url - The URL to check
 * @returns {boolean} - True if the URL is a PDF file
 */
export const isPdfFile = (url) => {
    if (!url) return false;
    return url.toLowerCase().includes('.pdf');
};

/**
 * Check if transfer proof is JSON format with required fields
 * @param {string} proof - The transfer proof data
 * @returns {boolean} - True if the proof is valid JSON with required fields
 */
export const isJsonTransferProof = (proof) => {
    if (!proof || typeof proof !== 'string') return false;
    try {
        const parsed = JSON.parse(proof);
        return parsed.finnetResponse && parsed.transferDetails;
    } catch {
        return false;
    }
};

/**
 * Parse JSON transfer proof data
 * @param {string} proof - The transfer proof data to parse
 * @returns {object|null} - Parsed proof data or null if invalid
 */
export const parseTransferProof = (proof) => {
    if (!proof) return null;
    try {
        return JSON.parse(proof);
    } catch {
        return null;
    }
};

/**
 * Determine the proof type based on the data
 * @param {string} proof - The transfer proof data
 * @returns {'json'|'image'|'pdf'|'file'|'unknown'} - The type of proof
 */
export const getProofType = (proof) => {
    if (!proof) return 'unknown';

    if (isJsonTransferProof(proof)) return 'json';
    if (isImageFile(proof)) return 'image';
    if (isPdfFile(proof)) return 'pdf';

    // Assume it's a file URL if it contains common URL patterns
    if (typeof proof === 'string' && (proof.startsWith('http') || proof.startsWith('/'))) {
        return 'file';
    }

    return 'unknown';
};